import type {ReactElement} from 'react';
import {useEffect, useId} from 'react';
import type {TabItems, TabKey} from '@Components/tabs';
import {Tabs, useSelectedTab} from '@Components/tabs';
import {deleteTabStateForId, selectTab} from '@Components/tabs/tabs-reducer';
import {ColorFillPropertyType} from '@Components/color-fill';
import {useAppDispatch} from '@/hooks';
import {ColorTypeTab, DEFAULT_GRADIENT_TYPE, NO_SELECTED_TAB_KEY} from './color-type-tabs.types';
import type {ColorTypeTabData, ColorTypeTabsProps} from './color-type-tabs.types';
import styles from './color-type-tabs.module.scss';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';

/** Solid / Gradient tab strip pinned to the top of the popup layout, drawn with the design-system Tabs. */
export function ColorTypeTabs({isTypeMixed, ...props}: ColorTypeTabsProps): ReactElement {
  const tabsId = `color-type-tabs-${useId()}`;
  const dispatch = useAppDispatch();
  const isGradient = props.type !== ColorFillPropertyType.SOLID;

  const getTabsData = (): ColorTypeTabData[] => {
    return [
      {id: ColorTypeTab.SOLID, text: window.i18next.t('pmwjs_solid_color'), testId: PosterEditorTestId.COLOR_TYPE_TAB_SOLID},
      {id: ColorTypeTab.GRADIENT, text: window.i18next.t('pmwjs_background_gradient'), testId: PosterEditorTestId.COLOR_TYPE_TAB_GRADIENT},
    ];
  };

  const getTabKey = (tabId: ColorTypeTab): TabKey => {
    return (
      getTabsData().find((tab) => {
        return tab.id === tabId;
      })?.testId ?? NO_SELECTED_TAB_KEY
    );
  };

  const getSelectedTabKey = (): TabKey => {
    if (isTypeMixed) {
      return NO_SELECTED_TAB_KEY;
    }
    return getTabKey(isGradient ? ColorTypeTab.GRADIENT : ColorTypeTab.SOLID);
  };

  const selectedTabKey = getSelectedTabKey();
  const tabKeyInStore = useSelectedTab(tabsId, selectedTabKey);

  // Tabs keep their selection in the store, but the fill type is the source of truth: undo, a mixed selection or a
  // rejected change must move the underline too.
  useEffect((): void => {
    if (tabKeyInStore !== selectedTabKey) {
      dispatch(selectTab(tabsId, selectedTabKey));
    }
  }, [tabKeyInStore, selectedTabKey]);

  useEffect(() => {
    return (): void => {
      dispatch(deleteTabStateForId(tabsId));
    };
  }, []);

  const onTabClick = (tabKey: TabKey): void => {
    const clickedTab = getTabsData().find((tab) => {
      return tab.testId === tabKey;
    });
    if (!clickedTab || tabKey === selectedTabKey) {
      return;
    }
    props.onChangeType(clickedTab.id === ColorTypeTab.GRADIENT ? DEFAULT_GRADIENT_TYPE : ColorFillPropertyType.SOLID);
  };

  const getTabItems = (): TabItems => {
    return getTabsData().map((tab) => {
      return {
        tabKey: tab.testId,
        text: tab.text,
        textClassName: `capitalize-sentence ${styles.colorTypeTabLabel}`,
        className: styles.colorTypeTabItem,
      };
    });
  };

  return (
    <div className={styles.colorTypeTabStrip}>
      <Tabs id={tabsId} selectedTab={selectedTabKey} tabItems={getTabItems()} shouldTabsHaveEqualWidth className={`${styles.colorTypeTabsBar} gap-2`} onTabClick={onTabClick} />
    </div>
  );
}
